import Papa from 'papaparse';

import type { AdminEnrollOutcome, AdminRemoveOutcome, ClaimCampaign } from '~/services/LuxLoyalty';

/** Most customer ids the admin-enroll / admin-remove endpoints accept per request. */
export const ADMIN_ENROLLMENT_BATCH_SIZE = 500;

/**
 * Pause between batches. Each request publishes its audit events after responding, so sending the
 * next batch straight away would stack that background work up on the service.
 */
export const ADMIN_ENROLLMENT_BATCH_PAUSE_MS = 1000;

/** Attempts per batch for network errors and 5xx responses. Resending is safe: the endpoints are idempotent. */
export const ADMIN_ENROLLMENT_MAX_ATTEMPTS = 3;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CUSTOMER_ID_HEADER_PATTERN = /^(customer_?id|customer_?bk)$/i;

export type AdminEnrollmentMode = 'enroll' | 'remove';

/** NOT_SENT is local: the run stopped (cancelled or a request was rejected) before this customer's batch was sent. */
export type AdminEnrollmentRowOutcome = AdminEnrollOutcome | AdminRemoveOutcome | 'NOT_SENT';

export interface AdminEnrollmentRowResult {
  customerId: string;
  outcome: AdminEnrollmentRowOutcome;
  enrollmentId?: string;
  /** Why the row failed or wasn't sent, when the whole batch errored. */
  error?: string;
}

export interface ParsedCustomerIds {
  /** Unique, lower-cased customer ids, in input order. */
  customerIds: Array<string>;
  /** Values that aren't customer ids (UUIDs). */
  invalidValues: Array<string>;
  duplicateCount: number;
}

/**
 * Extracts customer ids from pasted text or a CSV file.
 *
 * A CSV whose header has a `customer_id` (or `customerId` / `customer_bk`) column is read by that
 * column, so exports with extra columns work as-is. Anything else is treated as a list of ids
 * separated by new lines, commas, semicolons, tabs or spaces.
 */
export function parseCustomerIds(text: string): ParsedCustomerIds {
  const values = readCustomerIdColumn(text) ?? text.split(/[\s,;]+/);

  const seen = new Set<string>();
  const customerIds: Array<string> = [];
  const invalidValues: Array<string> = [];
  let duplicateCount = 0;

  for (const raw of values) {
    const value = raw.trim().replace(/^["']|["']$/g, '');
    if (!value || CUSTOMER_ID_HEADER_PATTERN.test(value)) {
      continue;
    }
    if (!UUID_PATTERN.test(value)) {
      invalidValues.push(value);
      continue;
    }
    const customerId = value.toLowerCase();
    if (seen.has(customerId)) {
      duplicateCount += 1;
      continue;
    }
    seen.add(customerId);
    customerIds.push(customerId);
  }

  return { customerIds, invalidValues, duplicateCount };
}

/** Values of the customer id column when the first line is a CSV header naming one, otherwise null. */
function readCustomerIdColumn(text: string): Array<string> | null {
  const firstLine = text.trimStart().split(/\r?\n/, 1)[0] ?? '';
  const header = Papa.parse<Array<string>>(firstLine).data[0] ?? [];
  const column = header.find((cell) => CUSTOMER_ID_HEADER_PATTERN.test(cell.trim()));
  if (!column) {
    return null;
  }

  const { data } = Papa.parse<Record<string, string>>(text.trim(), { header: true, skipEmptyLines: true });
  return data.map((row) => row[column] ?? '');
}

/**
 * Why bulk enrolment is blocked for this campaign, or null when it's allowed.
 *
 * Bulk uploads need the enrolment and reminder emails off: at this volume the enrolment emails
 * would flood the notification service, and the reminder run 14 days later would try to send them
 * all in one go. Scripts can still call the API directly.
 */
export function getBulkEnrollBlockedReason(campaign: Pick<ClaimCampaign, 'suppressedEmails'>): string | null {
  const suppressed = campaign.suppressedEmails ?? [];
  if (suppressed.includes('ENROLLMENT') && suppressed.includes('REMINDER')) {
    return null;
  }
  return 'Bulk enrolment needs the enrolment and reminder emails disabled on this campaign. Edit the campaign to turn them off first.';
}

export interface AdminEnrollmentProgress {
  processed: number;
  total: number;
}

export interface RunAdminEnrollmentOptions {
  /** Sends one batch (≤ batchSize ids) and returns the per-customer results. */
  sendBatch: (customerIds: Array<string>) => Promise<Array<Omit<AdminEnrollmentRowResult, 'error'>>>;
  onProgress?: (progress: AdminEnrollmentProgress) => void;
  /** Checked between batches; a batch already sent always finishes. */
  signal?: AbortSignal;
  /** Injected so tests don't wait. */
  sleep?: (ms: number) => Promise<void>;
  batchSize?: number;
  pauseMs?: number;
}

export interface RunAdminEnrollmentResult {
  /** One row per requested customer, in request order. */
  results: Array<AdminEnrollmentRowResult>;
  /** Set when the run stopped before every batch was sent. */
  stopReason?: string;
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Network errors (no status), 429 and 5xx are worth retrying; other 4xx mean the request itself is wrong. */
function isRetryable(error: unknown): boolean {
  const status = (error as { status?: number } | null)?.status;
  return status === undefined || status === 429 || status >= 500;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unknown error';
}

/**
 * Sends customer ids to admin-enroll / admin-remove one batch at a time.
 *
 * - Batches are sent strictly in sequence with a pause between them, never in parallel.
 * - A batch that fails with a network error or 5xx is retried; if it still fails its customers are
 *   reported FAILED and the run moves on (they can be resent from the results file).
 * - Any other error (e.g. 400 because the campaign isn't active) would fail every batch the same
 *   way, so the run stops and the remaining customers are reported NOT_SENT.
 * - Cancelling stops before the next batch.
 */
export async function runAdminEnrollmentBatches(
  customerIds: Array<string>,
  options: RunAdminEnrollmentOptions,
): Promise<RunAdminEnrollmentResult> {
  const {
    sendBatch,
    onProgress,
    signal,
    sleep = defaultSleep,
    batchSize = ADMIN_ENROLLMENT_BATCH_SIZE,
    pauseMs = ADMIN_ENROLLMENT_BATCH_PAUSE_MS,
  } = options;

  const results: Array<AdminEnrollmentRowResult> = [];
  const notSent = (from: number, reason: string) =>
    customerIds.slice(from).map(
      (customerId): AdminEnrollmentRowResult => ({
        customerId,
        outcome: 'NOT_SENT',
        error: reason,
      }),
    );

  for (let start = 0; start < customerIds.length; start += batchSize) {
    if (signal?.aborted) {
      return { results: [...results, ...notSent(start, 'Cancelled')], stopReason: 'Cancelled' };
    }
    if (start > 0) {
      await sleep(pauseMs);
    }

    const batch = customerIds.slice(start, start + batchSize);
    for (let attempt = 1; ; attempt += 1) {
      try {
        results.push(...(await sendBatch(batch)));
        break;
      } catch (error) {
        if (!isRetryable(error)) {
          const reason = errorMessage(error);
          return { results: [...results, ...notSent(start, reason)], stopReason: reason };
        }
        if (attempt >= ADMIN_ENROLLMENT_MAX_ATTEMPTS) {
          const reason = errorMessage(error);
          results.push(
            ...batch.map((customerId): AdminEnrollmentRowResult => ({ customerId, outcome: 'FAILED', error: reason })),
          );
          break;
        }
        await sleep(pauseMs * 2 ** attempt);
      }
    }

    onProgress?.({ processed: Math.min(start + batchSize, customerIds.length), total: customerIds.length });
  }

  return { results };
}

/** Number of customers per outcome, for the summary. */
export function countOutcomes(
  results: Array<AdminEnrollmentRowResult>,
): Partial<Record<AdminEnrollmentRowOutcome, number>> {
  const counts: Partial<Record<AdminEnrollmentRowOutcome, number>> = {};
  for (const { outcome } of results) {
    counts[outcome] = (counts[outcome] ?? 0) + 1;
  }
  return counts;
}
